import React, { useEffect, useState } from 'react';
import {
  CpuChipIcon,
  CircleStackIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { usePM2Monitor } from '../hooks/usePM2Monitor';

/**
 * Component to display PM2 Node processes monitoring
 */
export function PM2Monitor() {
  const { processes, loading, error, getPM2Metrics, lastUpdate } = usePM2Monitor();
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Get initial data
    getPM2Metrics();

    // Refresh every 5 seconds
    const interval = setInterval(getPM2Metrics, 5000);
    return () => clearInterval(interval);
  }, [getPM2Metrics]);

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500';
    if (percentage >= 60) return 'bg-orange-500';
    if (percentage >= 40) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getStatusBadge = (percentage) => {
    if (percentage >= 80) return { label: 'Crítico', color: 'text-red-700' };
    if (percentage >= 60) return { label: 'Alto', color: 'text-orange-700' };
    if (percentage >= 40) return { label: 'Médio', color: 'text-yellow-700' };
    return { label: 'Normal', color: 'text-green-700' };
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'online':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case 'stopped':
        return <ExclamationCircleIcon className="w-5 h-5 text-yellow-600" />;
      case 'errored':
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      default:
        return <CircleStackIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'online':
        return 'bg-green-50';
      case 'stopped':
        return 'bg-yellow-50';
      case 'errored':
        return 'bg-red-50';
      default:
        return 'bg-gray-50';
    }
  };

  const parseUptime = (uptimeObj) => {
    if (!uptimeObj) return 'N/A';
    if (typeof uptimeObj === 'string') return uptimeObj;
    const { days, hours, minutes } = uptimeObj;
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (loading && processes.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando processos do PM2...</p>
        </div>
      </div>
    );
  }

  if (error && processes.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 m-6">
        <p className="text-red-700">Erro ao carregar dados: {error}</p>
        <button
          onClick={getPM2Metrics}
          className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  const totalProcesses = processes.length;
  const onlineProcesses = processes.filter((p) => p.status.toLowerCase() === 'online').length;
  const avgCPU = processes.length > 0 ? processes.reduce((sum, p) => sum + parseFloat(p.cpu), 0) / processes.length : 0;

  // Filter processes based on search term
  const filteredProcesses = processes.filter((proc) => {
    if (!searchTerm) return true;
    
    const term = searchTerm.toLowerCase();
    return (
      (proc.name && proc.name.toLowerCase().includes(term)) ||
      (proc.pid && proc.pid.toString().includes(term)) ||
      (proc.script && proc.script.toLowerCase().includes(term))
    );
  });

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Monitor Node</h2>
          <p className="text-sm text-gray-500 mt-1">
            Monitoramento em tempo real dos processos Node.js via PM2
            {lastUpdate && ` - Atualizado em ${lastUpdate.toLocaleTimeString('pt-BR')}`}
          </p>
        </div>
        <button
          onClick={getPM2Metrics}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
        >
          <ArrowPathIcon className="w-5 h-5" />
          Atualizar
        </button>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome do processo, PID ou script..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        {searchTerm && (
          <p className="text-sm text-gray-600 mt-2">
            Encontrados: <strong>{filteredProcesses.length}</strong> de <strong>{processes.length}</strong> processos
          </p>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-gray-600 text-sm">Total de Processos</p>
          <p className="text-3xl font-bold text-gray-900">{totalProcesses}</p>
          <p className="text-xs text-green-600 mt-1">
            {onlineProcesses} online
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-gray-600 text-sm">CPU Média</p>
          <p className="text-3xl font-bold text-gray-900">{avgCPU.toFixed(2)}%</p>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
            <div
              className={`h-2 rounded-full ${getProgressColor(avgCPU)}`}
              style={{ width: `${Math.min(avgCPU, 100)}%` }}
            ></div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-gray-600 text-sm">Taxa de Atualização</p>
          <p className="text-3xl font-bold text-gray-900">5s</p>
          <p className="text-xs text-gray-500 mt-1">
            {loading ? 'Atualizando...' : 'Pronto'}
          </p>
        </div>
      </div>

      {/* Processes List */}
      {filteredProcesses.length === 0 ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-700">
            {searchTerm
              ? `Nenhum processo encontrado para "${searchTerm}"`
              : 'Nenhum processo encontrado no PM2'}
          </p>
          <p className="text-sm text-yellow-600 mt-1">
            {searchTerm
              ? 'Tente ajustar sua busca'
              : 'Inicie alguns processos com PM2 para vê-los aqui'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProcesses.map((proc, idx) => {
            const cpuVal = parseFloat(proc.cpu);
            const cpuStatus = getStatusBadge(cpuVal);

            return (
              <div
                key={`${proc.pid}-${idx}`}
                className={`rounded-lg border border-gray-200 overflow-hidden transition hover:shadow-lg ${getStatusColor(proc.status)}`}
              >
                {/* Process Header */}
                <div className="bg-white border-b border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(proc.status)}
                      <div>
                        <h3 className="font-semibold text-gray-900">{proc.name}</h3>
                        <p className="text-xs text-gray-500">PID: {proc.pid}</p>
                      </div>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 capitalize`}
                    >
                      {proc.status}
                    </span>
                  </div>
                </div>

                {/* Process Details */}
                <div className="bg-white p-4 space-y-4">
                  {/* CPU and Memory Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* CPU */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CpuChipIcon className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-700">CPU</span>
                        </div>
                        <span className={`text-lg font-bold ${cpuStatus.color}`}>
                          {cpuVal.toFixed(2)}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${getProgressColor(cpuVal)}`}
                          style={{ width: `${Math.min(cpuVal, 100)}%` }}
                        ></div>
                      </div>
                      <p className={`text-xs ${cpuStatus.color} mt-1`}>{cpuStatus.label}</p>
                    </div>

                    {/* Memory */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CircleStackIcon className="w-4 h-4 text-purple-600" />
                          <span className="text-sm font-medium text-gray-700">Memória</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">
                          {proc.memory}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {(parseFloat(proc.memoryRaw || 0) / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-200"></div>

                  {/* Additional Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Uptime</p>
                      <p className="font-semibold text-gray-900">
                        {parseUptime(proc.uptime)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Restarts</p>
                      <p className="font-semibold text-gray-900">{proc.restarts}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Modo</p>
                      <p className="font-semibold text-gray-900 capitalize">
                        {proc.exec_mode}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Instâncias</p>
                      <p className="font-semibold text-gray-900">{proc.instances}</p>
                    </div>
                  </div>

                  {/* Script Info */}
                  <div className="bg-gray-50 rounded p-3 border border-gray-200">
                    <p className="text-xs text-gray-600">Script</p>
                    <p className="text-sm text-gray-900 font-mono truncate">
                      {proc.script}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Loading Indicator */}
      {loading && processes.length > 0 && (
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          Atualizando...
        </div>
      )}
    </div>
  );
}

export default PM2Monitor;
